
import React, { useRef, useState } from "react";
import "./browser_picking_list_so.css";
import { Button, Input, Space, Table, Spin, Alert, DatePicker, notification } from 'antd';
import type { InputRef, TableProps } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import type { ColumnType, ColumnsType } from 'antd/es/table';
import type { FilterConfirmProps } from 'antd/es/table/interface';
import Highlighter from 'react-highlight-words';
import useSWR from 'swr';
import { useAuth } from "../../context/AuthContext";
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
dayjs.extend(customParseFormat);


const backendUrl = process.env.REACT_APP_BACKEND_URL;

interface SalesMasterRecord {
    key: React.Key;
    id: number;
    so_no: string;
    sales_date: string;
    cs_name: string;
    close_order_reason?: string;
    user_approved?: string;
    sales_person?: string;
    status?: string;
    se_no?: string;
    trans_type?: string;
    order_type?: string;
    cs_code?: string;
    purchase_order_no?: string;
    top?: string;
    agreement_no?: string;
    credit_limit_approval_status?: string;
    sub_total?: number | string;
    item_disc?: number | string;
    total_a?: number | string;
    vat?: number | string;
    grand_total?: number | string;
    delivery_address1?: string;
    delivery_address2?: string;
    contact_person?: string;
    contact_phone?: string;
    deliver_via?: string;
    warehouse_group?: string;
    approval_remark?: string;
    outstanding_remark?: string;
    remark?: string;
    credit_limit_remaining?: number | string;
}

interface AmpUserRecord {
    id?: number;
    nama: string;
    kode_grup1: string;
}

type DataIndex = keyof SalesMasterRecord;

const fetcher = (url: string) => fetch(url).then((res) => {
    if (!res.ok) {
        throw new Error('Failed to fetch data');
    }
    return res.json();
});

const parseDate = (dateStr?: string): number => {
    if (!dateStr) return 0;
    const parts = dateStr.split('/');
    if (parts.length === 3) {
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10) - 1;
        const year = parseInt(parts[2], 10);
        const time = new Date(year, month, day).getTime();
        return isNaN(time) ? 0 : time;
    }
    return 0;
};

interface BrowserPickingListSoProps {
    onRowDoubleClick: (record: SalesMasterRecord) => void;
}


const BrowserPickingListSo: React.FC<BrowserPickingListSoProps> = ({ onRowDoubleClick }) => {
    const transformStyle = {
        transform: "scale(0.80)",
        transformOrigin: "top left",
        width: "125%",
    };


    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState<DataIndex | ''>('');
    const searchInput = useRef<InputRef>(null);
    const { userData } = useAuth();
    const { data: salesData, error } = useSWR<SalesMasterRecord[]>(`${backendUrl}/api/sales-master-SO`, fetcher);
    const { data: pickingLists } = useSWR<any[]>(`${backendUrl}/api/picking-lists`, fetcher);
    const { data: usersData } = useSWR<AmpUserRecord[]>(`${backendUrl}/api/users`, fetcher);
    const handleSearch = (
        selectedKeys: string[],
        confirm: (param?: FilterConfirmProps) => void,
        dataIndex: DataIndex,
    ) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
    };

    const handleReset = (clearFilters: () => void) => {
        clearFilters();
        setSearchText('');
        setSearchedColumn('');
    };

    const getColumnSearchProps = (dataIndex: DataIndex): ColumnType<SalesMasterRecord> => ({
        filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
            <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                {dataIndex === 'sales_date' ? (
                    <DatePicker
                        format="DD/MM/YYYY"

                        value={selectedKeys[0] ? dayjs(selectedKeys[0] as string, 'DD/MM/YYYY') : null}

                        onChange={(_, dateString) => setSelectedKeys(dateString ? [dateString] as React.Key[] : [])}


                        style={{ marginBottom: 8, display: 'block' }}
                    />
                ) : (
                    <Input
                        ref={searchInput}
                        placeholder={`Search ${dataIndex.replace('_', ' ')}`}
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        style={{ marginBottom: 8, display: 'block' }}
                    />
                )} { }
                <Space>
                    <Button
                        type="primary"
                        onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        icon={<SearchOutlined />}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Search
                    </Button>
                    <Button
                        onClick={() => clearFilters && handleReset(clearFilters)}
                        size="small"
                        style={{ width: 90 }}
                    >
                        Reset
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            confirm({ closeDropdown: false });
                            setSearchText((selectedKeys as string[])[0]);
                            setSearchedColumn(dataIndex);
                        }}
                    >
                        Filter
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            close();
                        }}
                    >
                        close
                    </Button>
                </Space>
            </div>
        ),
        filterIcon: (filtered: boolean) => (
            <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
        ),


        onFilter: (value, record) => {
            if (dataIndex === 'sales_person') {
                const user = usersData?.find((u) => u.kode_grup1 === record.sales_person);
                const salesmanName = user ? user.nama : (record.sales_person || '');
                return salesmanName.toLowerCase().includes((value as string).toLowerCase());
            }
            const recordValue = record[dataIndex];
            if (dataIndex === 'sales_date') {
                if (!recordValue) return false;
                const recordDate = dayjs(recordValue.toString(), 'DD/MM/YYYY');
                const filterDate = dayjs(value as string, 'DD/MM/YYYY');
                return recordDate.isValid() && filterDate.isValid() && recordDate.isSame(filterDate, 'day');
            }
            return recordValue
                ? recordValue.toString().toLowerCase().includes((value as string).toLowerCase())
                : false;
        },
        filterDropdownProps: {
            onOpenChange: (visible) => {
                if (visible && dataIndex !== 'sales_date') {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        },
        render: (text) =>
            searchedColumn === dataIndex ? (
                <Highlighter
                    highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
                    searchWords={[searchText]}
                    autoEscape
                    textToHighlight={text ? text.toString() : ''}
                />
            ) : (
                text
            ),
    });

    const columns: ColumnsType<SalesMasterRecord> = [
        {
            title: 'Salesman',
            dataIndex: 'sales_person',
            key: 'sales_person',
            width: '20%',
            ...getColumnSearchProps('sales_person'),
            render: (text: string) => {
                const user = usersData?.find((u) => u.kode_grup1 === text);
                const displayText = user ? user.nama : (text || ''); 
                return searchedColumn === 'sales_person' ? ( 
                    <Highlighter 
                        highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }} 
                        searchWords={[searchText]} 
                        autoEscape 
                        textToHighlight={displayText} 
                    /> 
                ) : ( 
                    displayText 
                );
            },
        },
        {
            title: 'PO No',
            dataIndex: 'purchase_order_no',
            key: 'purchase_order_no',
            width: '20%',
            ...getColumnSearchProps('purchase_order_no'),
        },
        {
            title: 'Sales Order No',
            dataIndex: 'so_no',
            key: 'so_no',
            width: 'auto',
            ...getColumnSearchProps('so_no'),
        },
        {
            title: 'Date',
            dataIndex: 'sales_date',
            key: 'sales_date',
            width: '20%',
            sorter: (a, b) => parseDate(a.sales_date) - parseDate(b.sales_date),
            ...getColumnSearchProps('sales_date'),
            render: (text: string) => {
                if (!text) return '';
                const parts = text.split('/');
                if (parts.length === 3) {
                    const day = parseInt(parts[0], 10);
                    const month = parseInt(parts[1], 10);
                    const year = parseInt(parts[2], 10);
                    const dateObject = new Date(year, month - 1, day);
                    if (!isNaN(dateObject.getTime())) {
                        return dateObject.toLocaleDateString('en-GB');
                    }
                }
                return text;
            },
        },
        {
            title: 'Customer',
            dataIndex: 'cs_name',
            key: 'cs_name',
            ...getColumnSearchProps('cs_name'),
        },
    ];

    if (error) return <Alert message="Error" description="Gagal memuat data sales order." type="error" showIcon />;
    if (!salesData) return <Spin tip="Memuat data..." size="large"><div style={{ minHeight: '200px' }} /></Spin>;
    const tableData = salesData
        .filter(item => item.so_no && item.so_no.trim() !== '' && item.warehouse_group === userData?.warehouse_code)
        .map(item => ({ ...item, key: item.id }))
        .sort((a, b) => parseDate(b.sales_date) - parseDate(a.sales_date));

    return (
        <div id="iqfi" style={transformStyle}>
            <div className="gjs-grid-row" id="ia37">
                <div className="gjs-grid-column" id="ibfl">
                    <Table<SalesMasterRecord>
                        columns={columns}
                        dataSource={tableData}
                        bordered
                        size="small"
                        scroll={{ x: 'max-content' }}
                        onRow={(record) => {
                            return {
                                onClick: () => {
                                    const existingDraft = pickingLists?.find(pl => pl.sales_order === record.so_no && pl.status === '1');
                                    if (existingDraft) {
                                        notification.warning({
                                            message: "Peringatan",
                                            description: "Data sudah di input dalam mode Draft harap selesaikan !",
                                        });
                                        return;
                                    }
                                    onRowDoubleClick(record);
                                },
                            };
                        }}
                    />
                </div>
            </div>
        </div>
    );
};

export default BrowserPickingListSo;